/**
 * 商談の関係者 (2026-10)。商談詳細ヘッダーと顧客詳細「商談」タブで共用。
 *
 *  - 担当者（社内）: assigneeUid
 *  - SA（営業パートナー）⇄ 紹介者: salesAgentUid / referrerContactId は同時に設定できないため 1 つの枠で切り替える。
 *    一方を設定すると、もう一方は同じ更新で外す (確認あり)。SA 側に「弊社紹介」(isCompanyReferral) のトグル
 *  - 親（所有者）: parentContactId
 * いずれも Callable updateDeal で保存。名前は listMemberDirectory (useAdminDirectory) と顧客マスタで解決。
 */
import { X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import { ContactPickerDialog } from '../dialogs/ContactPickerDialog';

import { Button } from '@/components/ui/button';
import { useAdminDirectory, type AdminDirectoryEntry } from '@/hooks/useAdmins';
import { useAuth } from '@/hooks/useAuth';
import { useUpdateDeal, type DealRecord } from '@/hooks/useDeals';
import { useMailContact } from '@/hooks/useMailContacts';

type UpdateFields = {
  assigneeUid?: string | null;
  salesAgentUid?: string | null;
  referrerContactId?: string | null;
  parentContactId?: string | null;
  isCompanyReferral?: boolean;
};

const COMPANY_REFERRAL_HELP =
  'ON (弊社紹介) のとき、入金時の SA 報酬はベース報酬率で計算されます。OFF (開拓顧客) のときは「報酬設定 > SA > 開拓顧客加算率」(その SA の個別設定に加算率があればそちら) がベース報酬率に上乗せされます。紹介者・社員の報酬には影響しません。';

export function DealRelationsBar({ deal }: { deal: DealRecord }) {
  const update = useUpdateDeal();
  const directory = useAdminDirectory();
  const { user } = useAuth();
  const parent = useMailContact(deal.parentContactId ?? null);
  const referrer = useMailContact(deal.referrerContactId ?? null);

  const initialMode = (): 'sa' | 'referrer' =>
    deal.referrerContactId && !deal.salesAgentUid ? 'referrer' : 'sa';
  const [mode, setMode] = useState<'sa' | 'referrer'>(initialMode);
  const [editing, setEditing] = useState<'assignee' | 'sa' | null>(null);
  const [picker, setPicker] = useState<'parent' | 'referrer' | null>(null);

  // 商談を切り替えたら、その商談の設定に合わせて SA / 紹介者 の表示を戻す
  useEffect(() => {
    setMode(initialMode());
    setEditing(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal.id]);

  const members = directory.data ?? [];
  const nameOf = (uid: string | null | undefined) => {
    if (!uid) return '';
    const a = members.find((m) => m.uid === uid);
    return a ? label(a) : '（名前を取得中）';
  };
  // 担当者（社内）候補: 外部 SA 以外の active / SA 候補: 営業部署 × 外部 の active
  const internalOptions = members.filter(
    (a) => a.level !== 'external' && a.resolvedStatus === 'active',
  );
  const saOptions = members.filter(
    (a) => a.dept === 'sales' && a.level === 'external' && a.resolvedStatus === 'active',
  );
  const saName = nameOf(deal.salesAgentUid);
  const referrerName = deal.referrerContactId ? (referrer.data?.name ?? '（名前を取得中）') : '';
  // 弊社紹介フラグの編集は社内メンバーのみ (外部 SA は閲覧のみ)
  const canEditCompanyReferral = user?.level !== 'external';

  const save = async (fields: UpdateFields, message: string) => {
    try {
      await update.mutateAsync({ id: deal.id, ...fields });
      toast.success(message);
      setEditing(null);
    } catch (err) {
      toast.error(`更新失敗: ${(err as Error).message}`);
    }
  };

  const saveSa = (uid: string | null) => {
    if (!uid) return void save({ salesAgentUid: null }, 'SA を未設定にしました');
    const name = label(members.find((m) => m.uid === uid));
    if (deal.referrerContactId) {
      if (
        !confirm(
          `紹介者「${referrerName}」を外して、SA を「${name}」にします。よろしいですか？（SA と紹介者は同時に設定できません）`,
        )
      ) {
        return;
      }
      return void save(
        { salesAgentUid: uid, referrerContactId: null },
        `SA を「${name}」にしました（紹介者は外しました）`,
      );
    }
    void save({ salesAgentUid: uid }, `SA を「${name}」にしました`);
  };

  const saveReferrer = (contactId: string) => {
    if (deal.salesAgentUid) {
      if (
        !confirm(
          `SA「${saName}」を外して、紹介者を設定します。よろしいですか？（SA と紹介者は同時に設定できません）`,
        )
      ) {
        return;
      }
      return void save(
        { referrerContactId: contactId, salesAgentUid: null },
        '紹介者を設定しました（SA は外しました）',
      );
    }
    void save({ referrerContactId: contactId }, '紹介者を設定しました');
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
      {/* 担当者（社内） */}
      <Box>
        <div
          className="text-[10px] text-[var(--text-muted)]"
          title="ダッシュボード「自分の今日のタスク」（入金待ち・停滞案件など）はこの担当者に表示されます。"
        >
          担当者（社内）
        </div>
        {editing === 'assignee' ? (
          <div className="flex items-center gap-1.5">
            <select
              autoFocus
              value={deal.assigneeUid ?? ''}
              onChange={(e) => {
                const v = e.target.value || null;
                void save(
                  { assigneeUid: v },
                  v ? `担当者を「${nameOf(v)}」にしました` : '担当者を未設定にしました',
                );
              }}
              disabled={update.isPending || directory.isLoading}
              aria-label="担当者（社内）"
              className="flex-1 min-w-0 h-8 px-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
            >
              <option value="">未設定（誰のタスクにも表示されません）</option>
              {internalOptions.map((a) => (
                <option key={a.uid} value={a.uid}>
                  {label(a)}
                </option>
              ))}
              {deal.assigneeUid && !internalOptions.some((a) => a.uid === deal.assigneeUid) && (
                <option value={deal.assigneeUid}>
                  {nameOf(deal.assigneeUid)}（現状値・要再確認）
                </option>
              )}
            </select>
            <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>
              閉じる
            </Button>
          </div>
        ) : (
          <Value
            text={deal.assigneeUid ? nameOf(deal.assigneeUid) : '未設定'}
            muted={!deal.assigneeUid}
            onChange={() => setEditing('assignee')}
          />
        )}
      </Box>

      {/* SA（営業パートナー）⇄ 紹介者 */}
      <Box>
        <div
          role="group"
          aria-label="SA と紹介者の切り替え"
          className="inline-flex self-start rounded-md border border-[var(--border)] overflow-hidden text-[10px]"
        >
          {(
            [
              ['sa', 'SA（営業パートナー）'],
              ['referrer', '紹介者'],
            ] as const
          ).map(([key, text]) => (
            <button
              key={key}
              type="button"
              aria-pressed={mode === key}
              onClick={() => {
                setMode(key);
                setEditing(null);
              }}
              className={`px-2 h-6 ${
                mode === key
                  ? 'bg-[var(--accent)] text-white font-semibold'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              {text}
            </button>
          ))}
        </div>

        {mode === 'sa' ? (
          <>
            {editing === 'sa' ? (
              <div className="flex items-center gap-1.5">
                <select
                  autoFocus
                  value={deal.salesAgentUid ?? ''}
                  onChange={(e) => saveSa(e.target.value || null)}
                  disabled={update.isPending || directory.isLoading}
                  aria-label="SA（営業パートナー）"
                  className="flex-1 min-w-0 h-8 px-2 text-xs rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
                >
                  <option value="">未設定</option>
                  {saOptions.map((a) => (
                    <option key={a.uid} value={a.uid}>
                      {label(a)}
                    </option>
                  ))}
                  {deal.salesAgentUid && !saOptions.some((a) => a.uid === deal.salesAgentUid) && (
                    <option value={deal.salesAgentUid}>{saName}（現状値・要再確認）</option>
                  )}
                </select>
                <Button variant="ghost" size="sm" onClick={() => setEditing(null)}>
                  閉じる
                </Button>
              </div>
            ) : (
              <Value
                text={deal.salesAgentUid ? saName : '未設定'}
                muted={!deal.salesAgentUid}
                onChange={() => setEditing('sa')}
                onClear={
                  deal.salesAgentUid
                    ? () => void save({ salesAgentUid: null }, 'SA を未設定にしました')
                    : undefined
                }
              />
            )}
            <label
              className="flex items-center justify-between gap-2 pt-1 border-t border-[var(--border)]"
              title={COMPANY_REFERRAL_HELP}
            >
              <span className="text-[11px] text-[var(--text-secondary)]">
                弊社紹介
                {!canEditCompanyReferral && (
                  <span className="ml-1 text-[10px] text-[var(--text-muted)]">（閲覧のみ）</span>
                )}
              </span>
              <input
                type="checkbox"
                checked={!!deal.isCompanyReferral}
                disabled={!canEditCompanyReferral || update.isPending}
                onChange={(e) =>
                  void save(
                    { isCompanyReferral: e.target.checked },
                    e.target.checked ? '弊社紹介として記録しました' : '弊社紹介を解除しました',
                  )
                }
                aria-label="弊社紹介"
                className="appearance-none relative w-9 h-5 rounded-full bg-[var(--bg-hover)] border border-[var(--border)] cursor-pointer transition-colors checked:bg-[var(--accent)] checked:border-[var(--accent)] before:absolute before:top-0.5 before:left-0.5 before:size-3.5 before:rounded-full before:bg-white before:shadow before:transition-transform checked:before:translate-x-4 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            </label>
            {deal.referrerContactId && (
              <p className="text-[10px] text-[var(--accent-amber)]">
                紹介者「{referrerName}」が設定されています。SA を選ぶと紹介者は外れます。
              </p>
            )}
          </>
        ) : (
          <>
            <Value
              text={deal.referrerContactId ? referrerName : '未設定'}
              muted={!deal.referrerContactId}
              onChange={() => setPicker('referrer')}
              onClear={
                deal.referrerContactId
                  ? () => void save({ referrerContactId: null }, '紹介者を未設定にしました')
                  : undefined
              }
            />
            {deal.salesAgentUid && (
              <p className="text-[10px] text-[var(--accent-amber)]">
                SA「{saName}」が設定されています。紹介者を選ぶと SA は外れます。
              </p>
            )}
          </>
        )}
      </Box>

      {/* 親（所有者） */}
      <Box>
        <div className="text-[10px] text-[var(--text-muted)]">親（所有者）</div>
        <Value
          text={
            deal.parentContactId
              ? (parent.data?.name ?? '（名前を取得中）')
              : '未設定（使用者 = 所有者）'
          }
          muted={!deal.parentContactId}
          onChange={() => setPicker('parent')}
          onClear={
            deal.parentContactId
              ? () => void save({ parentContactId: null }, '親（所有者）を未設定にしました')
              : undefined
          }
        />
      </Box>

      <ContactPickerDialog
        open={picker !== null}
        onOpenChange={(o) => {
          if (!o) setPicker(null);
        }}
        title={picker === 'referrer' ? '紹介者を選択' : '親 (所有者) を選択'}
        description="氏名・メール・ID で検索"
        excludeContactId={deal.contactId}
        onSelect={(contactId) => {
          if (picker === 'referrer') saveReferrer(contactId);
          else void save({ parentContactId: contactId }, '親（所有者）を設定しました');
        }}
      />
    </div>
  );
}

function label(a: AdminDirectoryEntry | undefined): string {
  if (!a) return '';
  return a.fullName || a.displayName || a.email;
}

function Box({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-[var(--border)] bg-[var(--bg-elevated)] px-3 py-2 min-w-0">
      {children}
    </div>
  );
}

function Value({
  text,
  muted,
  onChange,
  onClear,
}: {
  text: string;
  muted?: boolean;
  onChange: () => void;
  onClear?: () => void;
}) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span
        className={`text-sm truncate ${
          muted ? 'text-[var(--text-muted)]' : 'font-medium text-[var(--text-primary)]'
        }`}
      >
        {text}
      </span>
      <span className="inline-flex items-center gap-1.5 shrink-0">
        <button
          type="button"
          onClick={onChange}
          className="text-[11px] text-[var(--accent)] hover:underline"
        >
          変更
        </button>
        {onClear && (
          <button
            type="button"
            onClick={onClear}
            className="p-0.5 text-[var(--text-muted)] hover:text-[var(--accent-red)]"
            title="未設定にする"
            aria-label="未設定にする"
          >
            <X className="size-3.5" />
          </button>
        )}
      </span>
    </div>
  );
}
